import {Component, computed, inject, OnDestroy, OnInit, signal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {firstValueFrom} from 'rxjs';
import {SelectComponent} from '../../../_ui/components/controls/select/select.component';
import {CheckboxComponent} from '../../../_ui/components/controls/checkbox/checkbox.component';
import {ClosingCorrectionRunCardComponent} from './closing-correction-run-card.component';
import {ClosingCorrectionConfirmDialogComponent} from './closing-correction-confirm-dialog.component';
import {ClosingCorrectionHttpService} from '../_services/closing-corrections/closing-correction-http.service';
import {DataExportHttpService} from '../_services/exports/data-export-http.service';
import {FiscalJobStore} from '../_services/fiscal-jobs/fiscal-job.store';
import {FiscalJobItem, FiscalJobSummary} from '../_models/fiscal-jobs/fiscal-job.models';
import {
  defaultPeriod,
  FiscalExportRegister,
  registerLabel
} from '../_models/exports/data-export.models';
import {companyToday} from '../../../_shared/time/company-day';
import {CompanyTimeZoneService} from '../../../_shared/time/company-time-zone.service';
import {
  candidateTitle,
  CLOSING_CORRECTION_MAX_ITEMS,
  CLOSING_CORRECTION_REASON_MAX,
  ClosingCorrectionCandidate,
  closingCountLabel,
  correctionErrorMessage,
  localStateLabel,
  sortCandidates
} from '../_models/closing-corrections/closing-correction.models';

/**
 * „Kassenabschluss erneut übermitteln" (#771): pick filed cash point closings, give a reason, and start a run
 * that withdraws each one and files it again, rebuilt from the stored data, under a new reference.
 *
 * ### What it is for
 *
 * Closings filed while Bugs #750/#751 were live carry wrong data. The data we store is right, so the fix is
 * to file those days again. Doing that by hand, closing by closing, is a support ticket per day; this screen
 * lets the operator, or their tax adviser, do it in bulk and follow each closing's outcome.
 *
 * ### Why nothing starts without a reason and a confirmation
 *
 * A withdrawal cannot be undone. The reason is required because it goes on record with every withdrawal, and
 * the confirmation dialog lists what the operator is agreeing to. Both are checked here before a request is
 * sent; the server checks them again and has the final word.
 *
 * ### Why a closing already being re-transmitted cannot be picked
 *
 * A second run would withdraw what the first one just filed. Such a row stays visible, disabled, with the
 * reason next to it, so the reader is not left wondering where a day went.
 *
 * ### Runs are the engine's
 *
 * A started correction is a fiscal job of kind `closingCorrection`. The list below, the live updates over
 * `/hubs/fiscal-jobs` and the per-closing retry all come from {@link FiscalJobStore}. `ngOnInit` starts it and
 * `ngOnDestroy` stops it, because the store is root-provided and counts the screens using it (Bug #783).
 */
@Component({
  selector: 'legal-closing-corrections',
  standalone: true,
  imports: [
    FormsModule,
    SelectComponent,
    CheckboxComponent,
    ClosingCorrectionRunCardComponent,
    ClosingCorrectionConfirmDialogComponent
  ],
  templateUrl: './closing-corrections.component.html'
})
export class ClosingCorrectionsComponent implements OnInit, OnDestroy {
  private readonly companyTimeZone = inject(CompanyTimeZoneService);
  private readonly http = inject(ClosingCorrectionHttpService);
  private readonly exportsHttp = inject(DataExportHttpService);
  private readonly store = inject(FiscalJobStore);

  protected readonly reasonMax = CLOSING_CORRECTION_REASON_MAX;
  protected readonly maxItems = CLOSING_CORRECTION_MAX_ITEMS;

  /** The first business day of the filter, `yyyy-MM-dd`. */
  protected readonly from = signal(defaultPeriod().from);

  /** The last business day of the filter, inclusive, `yyyy-MM-dd`. */
  protected readonly to = signal(defaultPeriod().to);

  /**
   * Today on the COMPANY's calendar, the upper bound of both date fields (#813). Derived, never absent:
   * the service answers the server's default zone until the company's own has arrived, and an absent value
   * would write `max="undefined"`, which is no bound at all.
   */
  protected readonly today = computed(() => companyToday(this.companyTimeZone.timeZone()));

  protected readonly registers = signal<FiscalExportRegister[]>([]);

  /** The registers to cover, or empty for every register. */
  protected readonly selectedRegisterIds = signal<string[]>([]);

  /** The closings the filter found, in display order. */
  protected readonly candidates = signal<ClosingCorrectionCandidate[]>([]);

  /** True while the candidate list is being read. */
  protected readonly candidatesLoading = signal(false);

  /** True once a candidate read has answered, so "nothing found" is not shown before anything was asked. */
  protected readonly candidatesLoaded = signal(false);

  /** The ids of the ticked closings. */
  protected readonly selected = signal<ReadonlySet<string>>(new Set());

  /** The reason as typed. Sent trimmed. */
  protected readonly reason = signal('');

  protected readonly confirming = signal(false);
  protected readonly creating = signal(false);

  protected readonly retryingItemId = signal<string | null>(null);
  protected readonly openRunId = signal<string | null>(null);

  /**
   * Why reading the registers or the candidates failed, in German, or null. Rendered at the top of the
   * screen, because a read failure is not tied to one button (#866 moved the start/retry results out of here;
   * this stays for the two loads, which have no single place of their own to show at).
   */
  protected readonly message = signal<string | null>(null);
  protected readonly messageIsSuccess = signal(false);

  /**
   * What the last start attempt did, in German, or null. Rendered directly under "Erneut übermitteln" rather
   * than at the top of the page, so a refusal is not lost above a long candidate list (#866, found in the
   * 2026-09-23 fiscal run). Cleared as soon as another start is asked for.
   */
  protected readonly startResult = signal<string | null>(null);
  protected readonly startResultIsSuccess = signal(false);

  /**
   * What the last per-closing retry did, in German, or null. Rendered at the top of "Bisherige
   * Übermittlungen" rather than at the top of the page (#866). Cleared as soon as another retry starts.
   */
  protected readonly retryResult = signal<string | null>(null);
  protected readonly retryResultIsSuccess = signal(false);

  /** The engine's list, narrowed to corrections. The export screens filter these out the same way. */
  protected readonly runs = computed<FiscalJobSummary[]>(() =>
    this.store.jobs().filter(job => job.kind === 'closingCorrection'));

  protected readonly openRun = computed(() => {
    const open = this.store.openJob();
    return open && open.job.id === this.openRunId() ? open : null;
  });

  protected readonly runsLoading = this.store.listLoading;
  protected readonly live = this.store.live;

  /**
   * The registers the filter offers. A register never set up for fiscal record keeping has no closings, so it
   * is left out, the same way the data export leaves it out.
   */
  protected readonly selectableRegisters = computed(() => this.registers()
    .filter(r => r.registered)
    .map(r => ({id: r.id, name: registerLabel(r)})));

  /** The candidates that may be ticked: everything not already being corrected. */
  protected readonly selectableCandidates = computed(() =>
    this.candidates().filter(c => !c.correctionOutstanding));

  protected readonly selectedCount = computed(() => this.selected().size);

  protected readonly selectedCountLabel = computed(() => closingCountLabel(this.selectedCount()));

  /** Whether every tickable row is ticked. False on an empty list, so the header box is never checked over nothing. */
  protected readonly allSelected = computed(() => {
    const selectable = this.selectableCandidates();
    return selectable.length > 0 && selectable.every(c => this.selected().has(c.entryId));
  });

  /**
   * Whether both fields hold something the browser accepted.
   *
   * A native `<input type="date">` reports an empty value both for an untouched field and for a date it could
   * not parse — typing an impossible calendar day such as 31.09.2026 clears the field the same way leaving it
   * blank does. So this is also false on that kind of typo, and the template tells the two cases apart from
   * {@link periodValid} rather than blaming the day order for a day that does not exist.
   */
  protected readonly periodComplete = computed(() => !!this.from() && !!this.to());

  protected readonly periodValid = computed(() => this.periodComplete() && this.from() <= this.to());

  protected readonly trimmedReason = computed(() => this.reason().trim());

  protected readonly reasonTooLong = computed(() => this.trimmedReason().length > CLOSING_CORRECTION_REASON_MAX);

  protected readonly tooMany = computed(() => this.selectedCount() > CLOSING_CORRECTION_MAX_ITEMS);

  /** Whether „Erneut übermitteln" may be pressed. */
  protected readonly canStart = computed(() =>
    !this.creating()
    && this.selectedCount() > 0
    && !this.tooMany()
    && this.trimmedReason().length > 0
    && !this.reasonTooLong());

  /** Why the start button is disabled, in one sentence, or null when it is not. */
  protected readonly startHint = computed<string | null>(() => {
    if (this.creating()) return null;
    if (this.selectedCount() === 0) return 'Wählen Sie mindestens einen Kassenabschluss aus.';
    if (this.tooMany()) {
      return `Es können höchstens ${CLOSING_CORRECTION_MAX_ITEMS} Kassenabschlüsse auf einmal erneut ` +
        'übermittelt werden.';
    }
    if (this.trimmedReason().length === 0) return 'Geben Sie einen Grund für die erneute Übermittlung an.';
    if (this.reasonTooLong()) return `Der Grund darf höchstens ${CLOSING_CORRECTION_REASON_MAX} Zeichen lang sein.`;
    return null;
  });

  async ngOnInit(): Promise<void> {
    this.store.start();
    await Promise.all([
      this.store.refreshList('closingCorrection'),
      this.loadRegisters(),
      this.loadCandidates()
    ]);
  }

  /** Releases the store (Bug #783). Paired with {@link ngOnInit}'s `start()`. */
  ngOnDestroy(): void {
    this.store.stop();
  }

  protected async loadRegisters(): Promise<void> {
    try {
      this.registers.set(await firstValueFrom(this.exportsHttp.registers()));
    } catch (error) {
      this.registers.set([]);
      this.fail(error);
    }
  }

  /**
   * Reads the closings for the current filter.
   *
   * The selection is narrowed to what is still on screen and still tickable afterwards. A closing that left the
   * list, or that another run has started correcting in the meantime, must not be sent along unseen.
   */
  protected async loadCandidates(): Promise<void> {
    if (!this.periodValid()) return;

    this.candidatesLoading.set(true);
    try {
      const rows = await firstValueFrom(this.http.candidates({
        from: this.from(),
        to: this.to(),
        registerIds: this.selectedRegisterIds()
      }));
      this.candidates.set(sortCandidates(rows));
      const tickable = new Set(this.selectableCandidates().map(c => c.entryId));
      this.selected.set(new Set([...this.selected()].filter(id => tickable.has(id))));
      this.candidatesLoaded.set(true);
    } catch (error) {
      this.fail(error);
    } finally {
      this.candidatesLoading.set(false);
    }
  }

  /** Ticks or unticks one closing. A closing already being corrected is ignored. */
  protected toggleCandidate(candidate: ClosingCorrectionCandidate, checked: boolean): void {
    if (candidate.correctionOutstanding) return;
    const next = new Set(this.selected());
    if (checked) next.add(candidate.entryId);
    else next.delete(candidate.entryId);
    this.selected.set(next);
  }

  /** Ticks every tickable row on screen, or clears the selection. */
  protected toggleAll(checked: boolean): void {
    this.selected.set(checked ? new Set(this.selectableCandidates().map(c => c.entryId)) : new Set());
  }

  protected isSelected(candidate: ClosingCorrectionCandidate): boolean {
    return this.selected().has(candidate.entryId);
  }

  protected title(candidate: ClosingCorrectionCandidate): string {
    return candidateTitle(candidate);
  }

  protected stateLabel(candidate: ClosingCorrectionCandidate): string {
    return localStateLabel(candidate.localState);
  }

  /** Opens the confirmation. Nothing has been sent yet. */
  protected askToStart(): void {
    if (!this.canStart()) return;
    this.startResult.set(null);
    this.confirming.set(true);
  }

  protected cancelStart(): void {
    this.confirming.set(false);
  }

  /** Starts the run over the ticked closings and opens it. */
  protected async start(): Promise<void> {
    if (!this.canStart()) return;

    this.creating.set(true);
    this.startResult.set(null);

    try {
      const created = await firstValueFrom(this.http.create({
        // In display order, so the run's entries come back in the order the reader ticked them in.
        entryIds: this.candidates().filter(c => this.selected().has(c.entryId)).map(c => c.entryId),
        reason: this.trimmedReason()
      }));

      // The dialog goes first: clearing the selection below would otherwise show it counting zero closings
      // while the follow-up reads are still out.
      this.confirming.set(false);
      this.selected.set(new Set());
      this.reason.set('');

      // Read back through the store, so the list row and the opened run come from one read path.
      this.openRunId.set(created.job.id);
      await this.store.refreshList('closingCorrection');
      await this.store.open(created.job.id);
      // The started closings are now outstanding; re-reading shows them disabled rather than tickable.
      await this.loadCandidates();

      this.succeedStart(
        'Die erneute Übermittlung wurde gestartet. Den Stand jedes Kassenabschlusses sehen Sie unten.');
    } catch (error) {
      this.failStart(error);
    } finally {
      this.creating.set(false);
      // Closed here rather than on confirm, so the confirm button stays disabled while the call is in flight
      // and a refusal leaves the reader looking at its message.
      this.confirming.set(false);
    }
  }

  protected async toggleRun(jobId: string): Promise<void> {
    if (this.openRunId() === jobId) {
      this.openRunId.set(null);
      this.store.close();
      return;
    }

    this.openRunId.set(jobId);
    await this.store.open(jobId);
  }

  /**
   * Tries one closing of the opened run again, on its own. Never the whole run: the closings that went
   * through were filed under a new reference, and running them again would withdraw those.
   */
  protected async retryItem(item: FiscalJobItem): Promise<void> {
    const jobId = this.openRunId();
    if (!jobId || this.retryingItemId()) return;

    this.retryingItemId.set(item.id);
    this.retryResult.set(null);

    const accepted = await this.store.retryItem(jobId, item.id);
    this.retryingItemId.set(null);

    if (accepted) {
      this.succeedRetry('Der Kassenabschluss wird erneut übermittelt.');
      return;
    }

    this.failRetry(this.store.lastError());
  }

  protected onRegistersChanged(value: string | string[] | undefined): void {
    if (Array.isArray(value)) this.selectedRegisterIds.set(value);
    else if (typeof value === 'string') this.selectedRegisterIds.set([value]);
    else this.selectedRegisterIds.set([]);
  }

  /** Records a load refusal as a German sentence, chosen by its `ApiError` code. Never the server's description. */
  private fail(error: unknown): void {
    this.message.set(correctionErrorMessage(this.errorCode(error)));
    this.messageIsSuccess.set(false);
  }

  /** Records why starting the run failed, at the button rather than at the top of the page (Bug #866). */
  private failStart(error: unknown): void {
    this.startResult.set(correctionErrorMessage(this.errorCode(error)));
    this.startResultIsSuccess.set(false);
  }

  private succeedStart(text: string): void {
    this.startResult.set(text);
    this.startResultIsSuccess.set(true);
  }

  private succeedRetry(text: string): void {
    this.retryResult.set(text);
    this.retryResultIsSuccess.set(true);
  }

  /** Records why a per-closing retry failed, at the runs section rather than at the top of the page (Bug #866). */
  private failRetry(code: string | null): void {
    this.retryResult.set(correctionErrorMessage(code));
    this.retryResultIsSuccess.set(false);
  }

  /** The `ApiError` code an HTTP failure carries, or null when it carries none. */
  private errorCode(error: unknown): string | null {
    const body = (error as { error?: { code?: unknown } } | null)?.error;
    return typeof body?.code === 'string' ? body.code : null;
  }
}
